import React from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import TableCell from 'components/application/TableCell';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import TrainingName from 'components/training/TrainingName';
import TrainingNotesIcon from 'components/training/TrainingNotesIcon';
import TrainingStatus from 'components/training/TrainingStatus';

import { formatDate } from 'components/helpers/dates';
import { getTrainingStatus } from 'components/helpers/resources/training';

export default function TrainingRow({
  attachments,
  course,
  hasTrainingEditableAccess,
  isArchived,
  isHistorical,
  isOverrideActive,
  isRequired,
  renderActions,
  renderBookings,
  requiredSource,
  setModalBoxData,
  training,
  trainingStatus,
}) {
  const { user: currentUser } = useCurrentActor();

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const modalData = {
    training,
    course,
    attachments,
  };

  const handleNotesToggleClick = () => {
    setModalBoxData({ ...modalData, context: 'notes', loaded: true });
  };

  const handleAttachmentsToggleClick = () => {
    setModalBoxData({ ...modalData, context: 'attachments', loaded: true });
  };

  const currentTrainingStatus = getTrainingStatus({ training, course });

  return (
    <tr className='tw-group/row tw-h-[49px] hover:tw-bg-grey-025'>
      <TableCell
        justifyContent='center'
        style={isHistorical ? { borderTopColor: 'transparent' } : {}}
      >
        {trainingStatus && (
          <TrainingStatus
            currentTrainingStatus={trainingStatus}
            isRequired={isRequired}
            requiredSource={requiredSource}
          />
        )}
      </TableCell>
      <TableCell>
        <TrainingName
          attachments={attachments}
          course={course}
          isHistorical={isHistorical}
          isOverrideActive={isOverrideActive}
          isRequired={isRequired}
          onAttachmentsToggleClick={handleAttachmentsToggleClick}
          showEvidenceRequirement={hasTrainingEditableAccess && !isArchived}
          training={training}
        />
        {training && training.attributes.notes && (
          <span className='tw-ml-2'>
            <TrainingNotesIcon onNotesToggleClick={handleNotesToggleClick} />
          </span>
        )}
      </TableCell>
      <TableCell>
        <span className='truncated-text-container'>
          {training ?
            course.attributes.expires ?
              training.attributes.expiryDate ?
                formatDate({
                  date: training.attributes.expiryDate,
                  format: 'dd MMM yyyy',
                })
              : i18nTShared('dateMissing')
            : i18nTShared('doesNotExpire')
          : i18nTShared('missing')}
        </span>
      </TableCell>
      <TableCell>
        {renderBookings && (
          <div className='tw-flex'>
            {renderBookings({ currentTrainingStatus })}
          </div>
        )}
      </TableCell>
      {currentUser.attributes.accessType !== 'personnel' && (
        <TableCell>{renderActions && renderActions({ training })}</TableCell>
      )}
    </tr>
  );
}

TrainingRow.propTypes = {
  attachments: PropTypes.array,
  course: PropTypes.object.isRequired,
  hasTrainingEditableAccess: PropTypes.bool,
  isArchived: PropTypes.bool,
  isHistorical: PropTypes.bool,
  isOverrideActive: PropTypes.bool,
  isRequired: PropTypes.bool,
  renderActions: PropTypes.func,
  renderBookings: PropTypes.func,
  requiredSource: PropTypes.string,
  setModalBoxData: PropTypes.func,
  training: PropTypes.object,
  trainingStatus: PropTypes.string,
};

TrainingRow.defaultProps = {
  isHistorical: false,
  isArchived: false,
  isRequired: true,
  hasTrainingEditableAccess: true,
  attachments: [],
};
